import React, { useEffect, useState } from 'react';
import { getCurrentWindow } from '@tauri-apps/api/window';
import { isTauri } from '../../utils/tauri';
import { detectPlatform } from '../../constants/desktop';
import './TitleBar.css';

// Custom, branded window title bar for the desktop (Tauri) build only.
// The window is frameless (decorations: false), so this component draws the
// draggable bar + min/maximize/close controls, and invisible edge/corner
// handles restore native-style resizing (frameless windows lose it on Windows).
// Renders nothing in the browser build.
//
// macOS is the exception: tauri.macos.conf.json keeps decorations with
// titleBarStyle "Overlay", so the OS draws real traffic lights over this bar
// and handles resizing itself. There we render the branded bar only — drawing
// our own Windows-style controls next to the native ones would show both.

// Resize directions map to invisible handles around the window edge.
const HANDLES: { cls: string; dir: string }[] = [
  { cls: 'n', dir: 'North' },
  { cls: 's', dir: 'South' },
  { cls: 'e', dir: 'East' },
  { cls: 'w', dir: 'West' },
  { cls: 'ne', dir: 'NorthEast' },
  { cls: 'nw', dir: 'NorthWest' },
  { cls: 'se', dir: 'SouthEast' },
  { cls: 'sw', dir: 'SouthWest' },
];

const IS_MAC = detectPlatform() === 'macos';

export default function TitleBar(): React.ReactElement | null {
  const [maximized, setMaximized] = useState(false);

  useEffect(() => {
    if (!isTauri()) return;
    const win = getCurrentWindow();
    let unlisten: (() => void) | undefined;
    const sync = () => { win.isMaximized().then(setMaximized).catch(() => {}); };
    sync();
    // Keep the maximize/restore icon in sync when the user resizes/snaps.
    win.onResized(sync).then((fn) => { unlisten = fn; }).catch(() => {});
    return () => { if (unlisten) unlisten(); };
  }, []);

  if (!isTauri()) return null;

  const win = getCurrentWindow();

  const startResize = (dir: string) => (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (win as any).startResizeDragging(dir).catch(() => {});
  };

  return (
    <>
      <div
        className={`atlas-titlebar${IS_MAC ? ' atlas-titlebar--mac' : ''}`}
        data-tauri-drag-region
      >
        <div className="atlas-titlebar__brand" data-tauri-drag-region>
          <img src="/logo-g.svg" alt="" className="atlas-titlebar__mark" data-tauri-drag-region />
          <span className="atlas-titlebar__title" data-tauri-drag-region>Atlas</span>
        </div>

        {!IS_MAC && (
        <div className="atlas-titlebar__controls">
          <button
            type="button"
            className="atlas-titlebar__btn"
            aria-label="Minimize"
            onClick={() => win.minimize()}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
              <rect x="0" y="4.5" width="10" height="1" fill="currentColor" />
            </svg>
          </button>

          <button
            type="button"
            className="atlas-titlebar__btn"
            aria-label={maximized ? 'Restore' : 'Maximize'}
            onClick={() => win.toggleMaximize()}
          >
            {maximized ? (
              <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                <rect x="0.5" y="2.5" width="7" height="7" fill="none" stroke="currentColor" />
                <path d="M2.5 2.5 V0.5 H9.5 V7.5 H7.5" fill="none" stroke="currentColor" />
              </svg>
            ) : (
              <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                <rect x="0.5" y="0.5" width="9" height="9" fill="none" stroke="currentColor" />
              </svg>
            )}
          </button>

          <button
            type="button"
            className="atlas-titlebar__btn atlas-titlebar__btn--close"
            aria-label="Close"
            onClick={() => win.close()}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
              <path d="M0.5 0.5 L9.5 9.5 M9.5 0.5 L0.5 9.5" stroke="currentColor" strokeWidth="1.1" />
            </svg>
          </button>
        </div>
        )}
      </div>

      {/* Invisible resize handles — restore native-style edge/corner resizing.
          Not needed on macOS, where the window keeps its native decorations. */}
      {!IS_MAC && HANDLES.map((h) => (
        <div
          key={h.cls}
          className={`atlas-resize atlas-resize--${h.cls}`}
          onMouseDown={startResize(h.dir)}
        />
      ))}
    </>
  );
}
